/**
 * What a pre-pour row's More cards show, read from one keyon GetPatientMeds record.
 *
 * GetPatientMeds is `medications.* + orders.* + paproviders.* + patpharmacy.*` plus aliases
 * (keyon SqlClass::findpatientactiveMedOrders). The page used to read new_medications' key
 * names (orderDate, diagDescription, instructions, …), which this endpoint never returns, so
 * every card read "Not recorded" although keyon had the data.
 */
export function medRecordCard(r) {
  return {
    orderDate: r.orderdate || '',
    diagnosis: r.diagnosis || r.diagnose_code || '',
    instructions: r.instruction || '',
    startDate: r.med_startdate || '',
    endDate: r.med_enddate || '',
    // providername is CONCAT_WS over paproviders: '' when the order has no provider row joined,
    // so the order's own primary_physician is the fallback, not a blank card
    prescriber: r.providername || r.primary_physician || '',
    prescriberNpi: r.providernpi || '',
    prescriberPhone: r.provideroffice || '',
    orderedBy: r.writer || '',
    pharmacy: pharmacyOf(r),
  };
}

/**
 * GetPatientMeds filters on `medications.medadmin = 1` only. Pre-pour is its own checkbox on the
 * medication (Add Meds, Orders), so a medication given but never pre-poured stays off the sheet.
 * MySQL tinyint arrives as a number, but older PDO paths send it as a string.
 */
export function isPrepourRecord(r) {
  return Number(r.prepour) === 1;
}

/**
 * Added from Add Meds as "Non-prescription": keyon created it with no order (medications.nonprescription,
 * order_number NULL). Records from before the column existed are prescriptions.
 */
export function isNonPrescriptionRecord(r) {
  return Number(r.nonprescription) === 1;
}

/**
 * New and Pending are read from keyon, never set on the sheet (decided 2026-09-29). Pending: the medication's
 * order still waits for a signature (orders.status Pending — a nurse's order until a provider signs). New:
 * nursing tagged today's doses new (medlogtimes.statustag, returned as tagstatus). Null otherwise.
 */
export function derivedStatusOf(r) {
  if (Number(r.nonprescription) !== 1 && /^pending$/i.test(String(r.orderstatus || ''))) return 'Pending';
  if (/(^|,)\s*new\s*(,|$)/i.test(String(r.tagstatus || ''))) return 'New';
  return null;
}

// patpharmacy is LEFT JOINed on the order number, so an order without a pharmacy comes back as nulls
function pharmacyOf(r) {
  const name = r.pharmacyname || r.pharmacy || '';
  if (!name) return null;
  return { name, address: r.pharmacyAddress || '', phone: r.pharmacyPhone || '', npi: r.pharmacyNpi || '' };
}

/**
 * Has the course ended by `day` (YYYY-MM-DD)? Only after its end date - the end date itself is
 * still given. GetPatientMeds keeps carrying today's slots for a course whose
 * end date has passed (a course runs until someone marks it Completed), so pre-pour offered and
 * poured it. '', 1970-01-01 / 1971-01-01 (keyon's "no end date") and 0000-00-00 never end.
 */
export function courseEndedOn(endDate, day) {
  const end = String(endDate || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(end) || end < '1980-01-01') return false;
  return day > end;
}
